import { useTranslation } from "@sagot/i18n";
import { Badge, Icon } from "@sagot/ui";
import { addonPriceOf, useAddonCatalog, useAddonPrice, useAgentList, useFormatters, yearContext, type BillingOverview } from "../shared";
import { AutoTopUp } from "./auto-topup";
import { heldUnits, storageText, unitsLeft } from "./billing-model";
import { UsageMeter } from "./usage-meter";

interface UsageCardProps {
  overview: BillingOverview;
  canManage: boolean;
  onAddSeats: () => void;
  onAddStorage: () => void;
}

const KNOWLEDGE_STORAGE = "knowledge_storage_1gb";

export function UsageCard({ overview, canManage, onAddSeats, onAddStorage }: UsageCardProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const agentNames = (useAgentList().data ?? []).map((agent) => agent.name);
  const seat = useAddonPrice("extra_seat");
  const catalog = useAddonCatalog().data;
  // "Add storage" only where the add-on is on sale for this plan and the cap is not reached.
  const storageOffer = catalog?.find((addon) => addon.key === KNOWLEDGE_STORAGE && addon.available_plans.includes(overview.plan.key));
  const canAddStorage = canManage && storageOffer !== undefined && unitsLeft(KNOWLEDGE_STORAGE, heldUnits(overview.addons, KNOWLEDGE_STORAGE)) !== 0;
  const { credits, playground, agents, seats, storage_mb: storage, workflow_runs: runs } = overview;
  const of = (limit: number | null) => (limit === null ? t("billing.usage.unlimited") : `/ ${fmt.number(limit)}`);
  const resets = overview.period_end ? fmt.shortDate(overview.period_end) : "—";
  return (
    <section className="card xl:col-span-2" aria-labelledby="usage-title">
      <div className="card-header">
        <div>
          <h2 id="usage-title" className="card-title">
            {t("billing.usage.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("billing.usage.cycle", { day: overview.day_of_cycle, days: overview.cycle_days, date: resets })}</p>
        </div>
        <span className="text-xs font-semibold text-fg-muted">
          {t("billing.usage.projectedLabel")} <span className="text-fg">{t("billing.usage.projected", { count: credits.projected, value: fmt.number(credits.projected) })}</span>
        </span>
      </div>
      <div className="card-body grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
        <UsageMeter
          label={t("billing.usage.messages")}
          used={credits.used}
          limit={credits.allowance}
          projected={credits.projected}
          usedLabel={fmt.number(credits.used)}
          limitLabel={of(credits.allowance)}
          hintWarns={credits.projected_overage > 0}
          hint={credits.projected_overage > 0 ? t("billing.usage.overPace", { value: fmt.number(credits.projected_overage), date: resets }) : t("billing.usage.remaining", { value: fmt.number(credits.remaining) })}
        />
        <UsageMeter label={t("billing.usage.agents")} used={agents.used} limit={agents.limit} usedLabel={fmt.number(agents.used)} limitLabel={of(agents.limit)} hint={agentNames.length ? agentNames.join(", ") : undefined} />
        <UsageMeter
          label={t("billing.usage.storage")}
          used={storage.used}
          limit={storage.limit}
          usedLabel={storageText(storage.used)}
          limitLabel={storage.limit === null ? t("billing.usage.unlimited") : `/ ${storageText(storage.limit)}`}
          hint={
            canAddStorage ? (
              <>
                <button type="button" className="font-semibold text-primary-ink hover:underline" onClick={onAddStorage}>
                  {t("billing.usage.addStorage")}
                </button>{" "}
                {t("billing.usage.perGb", { price: fmt.money(storageOffer.interval_price, storageOffer.currency), ...yearContext(addonPriceOf(storageOffer).per) })}
              </>
            ) : undefined
          }
        />
        <UsageMeter
          label={t("billing.usage.seats")}
          used={seats.used}
          limit={seats.limit}
          usedLabel={fmt.number(seats.used)}
          limitLabel={of(seats.limit)}
          hint={
            <>
              {canManage ? (
                <button type="button" className="font-semibold text-primary-ink hover:underline" onClick={onAddSeats}>
                  {t("billing.usage.addSeats")}
                </button>
              ) : (
                t("billing.usage.addSeats")
              )}{" "}
              {seat ? t("billing.usage.perSeat", { price: fmt.money(seat.intervalPrice, seat.currency), ...yearContext(seat.per) }) : null}
            </>
          }
        />
        <UsageMeter
          label={t("billing.usage.playground")}
          used={playground.used}
          limit={playground.allowance}
          usedLabel={fmt.number(playground.used)}
          limitLabel={of(playground.allowance)}
          hint={t("billing.usage.playgroundHint")}
        />
        <UsageMeter
          label={t("billing.usage.workflowRuns")}
          used={runs.used}
          limit={runs.limit}
          usedLabel={fmt.number(runs.used)}
          limitLabel={of(runs.limit)}
          hint={t("billing.usage.workflowRunsHint")}
        />
        <div className="flex items-center justify-between rounded-md bg-surface-2 px-3 py-2.5 sm:self-end">
          <span className="flex items-center gap-2 text-sm font-bold">
            <Icon name="mic" className="size-4 text-fg-subtle" />
            {t("billing.usage.voice")}
          </span>
          <Badge>{t("billing.usage.notOnPlan")}</Badge>
        </div>
      </div>
      <div className="flex flex-col gap-3 border-t border-line px-5 py-4 md:flex-row md:items-center">
        <p className="min-w-0 flex-1 text-xs text-fg-muted">
          <strong className="text-fg">{t("billing.usage.overageTitle")}</strong> {t("billing.usage.overageBody", { value: credits.allowance ? fmt.number(credits.allowance) : "—" })}
        </p>
        <AutoTopUp settings={overview.auto_topup} canManage={canManage} />
      </div>
    </section>
  );
}
